import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { axios, API, logError } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import { getImageUrl } from "./ToyCard";

/**
 * Above-the-fold engagement strip showing the 3 most recent free donations
 * with a one-tap "Claim free" CTA. Designed to convert first-time visitors
 * by showcasing zero-risk transactions.
 */
export const FreeToysStrip = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/listings?transaction_type=donate&limit=3`);
        if (!cancelled) setItems(res.data || []);
      } catch (err) {
        logError("FreeToysStrip: failed to load donations", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const claim = async (listing) => {
    if (!user || !token) {
      toast("Sign in to claim a free toy");
      navigate("/auth");
      return;
    }
    if (!["pro", "business"].includes(user.subscription_tier)) {
      toast("Claiming free toys is a Pro perk — upgrade to claim with shipping covered.");
      navigate("/pricing");
      return;
    }
    // Donations now require shipping selection — route to the listing detail page where
    // the user can pick a shipping service + enter address via the standard checkout modal.
    navigate(`/listing/${listing.id}`);
  };

  if (loading || items.length === 0) return null;

  return (
    <section
      className="brutalist-card relative overflow-hidden mb-8 p-6 sm:p-8"
      style={{ background: "linear-gradient(135deg, #ECFDF5 0%, #FEF3C7 100%)" }}
      data-testid="free-toys-strip"
    >
      {/* Decorative accents */}
      <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#10B981]/10 rounded-full pointer-events-none" />
      <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-[#FFD93D]/20 rounded-full pointer-events-none" />

      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span
            className="w-11 h-11 bg-[#10B981] rounded-full flex items-center justify-center text-white border-2 border-gray-900 shadow-[3px_3px_0px_0px_rgba(17,24,39,1)]"
            aria-hidden="true"
          >
            <Gift className="w-5 h-5" />
          </span>
          <div>
            <h2
              className="text-2xl sm:text-3xl font-black flex items-center gap-2"
              style={{ fontFamily: "Outfit" }}
              data-testid="free-toys-title"
            >
              Free Toys Today
              <Sparkles className="w-5 h-5 text-[#FFD93D]" />
            </h2>
            <p className="text-sm text-gray-700">
              {items.length} pre-loved {items.length === 1 ? "toy" : "toys"} ready for a new home — claim in one tap.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/browse?is_donation=true")}
          className="brutalist-btn-secondary text-sm py-2 px-4 self-start sm:self-auto inline-flex items-center gap-2"
          data-testid="free-toys-view-all"
        >
          View all <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((listing) => (
          <article
            key={listing.id}
            className="bg-white border-2 border-gray-900 rounded-xl overflow-hidden shadow-[4px_4px_0px_0px_rgba(17,24,39,1)] flex flex-row sm:flex-col"
            data-testid={`free-toy-card-${listing.id}`}
          >
            <button
              type="button"
              onClick={() => navigate(`/listing/${listing.id}`)}
              className="block w-28 sm:w-full sm:h-40 shrink-0 relative"
              aria-label={`View ${listing.title}`}
            >
              <img
                src={getImageUrl(listing.images)}
                alt={listing.title}
                className="w-full h-full object-cover aspect-square sm:aspect-auto"
                onError={(e) => {
                  e.target.src =
                    "https://images.unsplash.com/photo-1596068587619-e4b11c7a3488?w=400&h=300&fit=crop";
                }}
              />
              <span className="absolute top-2 left-2 bg-[#10B981] text-white text-[10px] font-black px-2 py-1 rounded-full border-2 border-gray-900">
                FREE
              </span>
            </button>
            <div className="p-3 sm:p-4 flex-1 flex flex-col gap-2">
              <h3
                className="font-bold text-sm sm:text-base line-clamp-1"
                style={{ fontFamily: "Outfit" }}
              >
                {listing.title}
              </h3>
              <p className="text-xs text-gray-500 line-clamp-1">
                {listing.category} · {listing.condition}
              </p>
              <button
                type="button"
                onClick={() => claim(listing)}
                className="mt-auto bg-[#10B981] text-white font-black text-sm py-2 px-3 rounded-full border-2 border-gray-900 shadow-[3px_3px_0px_0px_rgba(17,24,39,1)] hover:translate-y-[-1px] active:translate-y-0 transition disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid={`claim-free-strip-${listing.id}`}
              >
                {user && !["pro", "business"].includes(user.subscription_tier) ? "Upgrade to claim" : "Claim free"}
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};
